import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { Workspace } from '@ext-bookmark/shared/types';
import { addWorkspace, updateWorkspace, deleteWorkspace } from '../lib/storage';
import { useSidebarTheme } from '../hooks/useSidebarTheme';
import { generateLetterIcon, renderIcon } from '../lib/iconLibrary';
import WorkspaceForm from './WorkspaceForm';

interface WorkspaceSidebarProps {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  onWorkspaceChange: (workspace: Workspace | null) => void;
  onDataChange: () => void;
  onSettingsClick?: () => void;
  /** 移动端：是否以覆盖层显示；桌面端忽略 */
  isMobile?: boolean;
  /** 移动端：覆盖层是否打开 */
  isOverlayOpen?: boolean;
  /** 移动端：关闭覆盖层 */
  onOverlayClose?: () => void;
}

const renderWorkspaceIcon = (workspace: Workspace): React.ReactNode => {
  if (!workspace.icon) {
    return (
      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
        {generateLetterIcon(workspace.name)}
      </div>
    );
  }

  const icon = workspace.icon;
  
  if (icon.type === 'letter') {
    return (
      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
        {generateLetterIcon(workspace.name)}
      </div>
    );
  }
  
  if (icon.type === 'icon-library' && icon.iconLibrary && icon.iconName) {
    const iconComponent = renderIcon(icon.iconLibrary, icon.iconName, 'w-6 h-6');
    if (iconComponent) {
      return iconComponent;
    }
  }
  
  if (icon.type === 'custom' && icon.customUrl) {
    return (
      <img src={icon.customUrl} alt="" className="w-6 h-6 rounded-lg object-cover" />
    );
  }
  
  return (
    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
      {generateLetterIcon(workspace.name)}
    </div>
  );
};

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  workspaces,
  currentWorkspace,
  onWorkspaceChange,
  onDataChange,
  onSettingsClick,
  isMobile = false,
  isOverlayOpen = false,
  onOverlayClose,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [contextMenu, setContextMenu] = useState<{ workspace: Workspace; x: number; y: number } | null>(null);
  
  const sidebarTheme = useSidebarTheme();
  const sidebarWidth = sidebarTheme.width;

  const effectiveWidth = useMemo(() => {
    return Math.max(sidebarWidth || 120, 50);
  }, [sidebarWidth]);

  const handleAddWorkspace = async (name: string) => {
    const newWorkspace = await addWorkspace({
      name,
      components: [],
    });
    setShowAddForm(false);
    onDataChange();
    onWorkspaceChange(newWorkspace);
    if (isMobile) onOverlayClose?.();
  };

  const handleUpdateWorkspace = async (name: string) => {
    if (editingWorkspace) {
      await updateWorkspace(editingWorkspace.id, { name });
      setEditingWorkspace(null);
      onDataChange();
    }
  };

  const handleDeleteWorkspace = async (workspace: Workspace) => {
    if (confirm(`确定要删除工作区 "${workspace.name}" 吗？此操作不可恢复。`)) {
      await deleteWorkspace(workspace.id);
      setContextMenu(null);
      onDataChange();
      if (currentWorkspace?.id === workspace.id) {
        const remainingWorkspaces = workspaces.filter(w => w.id !== workspace.id);
        onWorkspaceChange(remainingWorkspaces[0] || null);
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent, workspace: Workspace) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ workspace, x: e.clientX, y: e.clientY });
  };

  const handleClickOutside = () => {
    setContextMenu(null);
  };

  React.useEffect(() => {
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const backgroundColor = useMemo(() => {
    const hex = sidebarTheme.backgroundColor;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${sidebarTheme.opacity})`;
  }, [sidebarTheme]);

  const handleWorkspaceSelect = (workspace: Workspace) => {
    onWorkspaceChange(workspace);
    if (isMobile) onOverlayClose?.();
  };

  const sidebarContent = (
    <div 
      className="sidebar fixed left-0 top-0 bottom-0 flex flex-col overflow-x-hidden border-r z-30 transition-all duration-300"
      style={{ 
        width: `${effectiveWidth}px`,
        backgroundColor: backgroundColor,
        color: sidebarTheme.textColor,
        borderColor: sidebarTheme.borderColor,
        minWidth: '50px',
      }}
    >
      {/* 顶部：用户头像占位符 */}
      <div className="flex-shrink-0 w-full flex items-center justify-center py-4 border-b" style={{ borderColor: sidebarTheme.borderColor }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-colors" style={{ backgroundColor: sidebarTheme.hoverColor }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarTheme.textColor }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      </div>

      {/* 中间：工作区列表和添加按钮 */}
      <div className="flex-1 flex flex-col gap-1 w-full py-2 overflow-hidden">
        <div className="flex-1 flex flex-col gap-1 w-full overflow-y-auto overflow-x-hidden px-2">
          {(!workspaces || workspaces.length === 0) && !showAddForm && (
            <div className="px-2 py-8 text-center text-xs" style={{ color: sidebarTheme.textColor }}>
              暂无工作区
            </div>
          )}
        
          {(workspaces || []).map((workspace) => (
            <div
              key={workspace.id}
              className={`sidebar-item flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 ${
                currentWorkspace?.id === workspace.id ? 'active' : ''
              }`}
              onClick={() => handleWorkspaceSelect(workspace)}
              onContextMenu={(e) => handleContextMenu(e, workspace)}
              style={{
                color: sidebarTheme.textColor,
                backgroundColor: currentWorkspace?.id === workspace.id ? sidebarTheme.activeColor : 'transparent',
              }}
            >
              <div className="flex-shrink-0">
                {renderWorkspaceIcon(workspace)}
              </div>
              <div className="flex-1 min-w-0 text-sm font-medium truncate">
                {workspace.name}
              </div>
            </div>
          ))}
        </div>

        <div className="flex-shrink-0 px-2">
          <div 
            className="sidebar-item flex items-center justify-center py-2"
            onClick={() => setShowAddForm(true)}
            title="添加工作区"
            style={{
              color: sidebarTheme.textColor,
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        </div>
      </div>

      {/* 底部：书签、日历和设置按钮 */}
      <div className="flex-shrink-0 w-full flex flex-col items-center gap-2 py-4 border-t" style={{ borderColor: sidebarTheme.borderColor }}>
        <Link
          to="/bookmarks"
          className="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors no-underline"
          style={{ backgroundColor: sidebarTheme.hoverColor }}
          title="书签"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarTheme.textColor }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </Link>
        <Link
          to="/calendar"
          className="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors no-underline"
          style={{ backgroundColor: sidebarTheme.hoverColor }}
          title="日历"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarTheme.textColor }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </Link>
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
          style={{ backgroundColor: sidebarTheme.hoverColor }}
          onClick={() => {
            onSettingsClick?.();
            if (isMobile) onOverlayClose?.();
          }}
          title="设置"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarTheme.textColor }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
      </div>

      {/* 右键菜单 */}
      {contextMenu && (
        <div
          className="fixed z-50 glass rounded-xl py-2 min-w-[160px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              setEditingWorkspace(contextMenu.workspace);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            编辑
          </button>
          <button
            onClick={() => {
              handleDeleteWorkspace(contextMenu.workspace);
            }}
            className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors duration-200 cursor-pointer rounded-lg mx-1"
          >
            删除
          </button>
        </div>
      )}

      {/* 添加工作区模态框 */}
      {showAddForm && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" 
          onClick={() => setShowAddForm(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-2xl p-6 w-full max-w-md mx-4"
          >
            <WorkspaceForm
              onSave={(name) => {
                handleAddWorkspace(name);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* 编辑工作区模态框 */}
      {editingWorkspace && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" 
          onClick={() => setEditingWorkspace(null)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-2xl p-6 w-full max-w-md mx-4"
          >
            <WorkspaceForm
              workspace={editingWorkspace}
              onSave={(name) => {
                handleUpdateWorkspace(name);
                setEditingWorkspace(null);
              }}
              onCancel={() => setEditingWorkspace(null)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  if (isMobile) {
    if (!isOverlayOpen) return null;
    return (
      <>
        <div
          className="fixed inset-0 bg-black/50 z-20"
          onClick={onOverlayClose}
          aria-hidden="true"
        />
        {sidebarContent}
      </>
    );
  }

  return sidebarContent;
};

export default WorkspaceSidebar;
